import type { DerivedSiteVerdict } from '../../lib/portfolio/deriveSiteVerdict';
import type { VerdictTier } from '../../lib/reportVerdict';
import type { StatusPillVariant } from '../ui/status-pill';

export const VERDICT_DOT_SIZE_CLASSES = {
  xs: 'w-1.5 h-1.5',
  sm: 'w-2 h-2',
  md: 'w-2.5 h-2.5',
  lg: 'w-3 h-3',
} as const;

export type VerdictDotSize = keyof typeof VERDICT_DOT_SIZE_CLASSES;

export function bandColorClass(
  color: DerivedSiteVerdict['bandColor'] | null | undefined,
): string {
  if (color === 'green') return 'bg-primary';
  if (color === 'amber') return 'bg-warning';
  // Aurora: the CONDITIONAL-HIGH band re-skins onto the dedicated
  // --conditional-high token (was raw bg-orange-500) so it tracks the theme
  // (dark #fb923c / light #c2620e) instead of a fixed Tailwind palette stop.
  if (color === 'orange') return 'bg-conditional-high';
  if (color === 'red') return 'bg-danger';
  return 'bg-text-muted/30';
}

/**
 * Aurora verdict-dot glow. Returns the CSS variable that the shared
 * `.glow-band` recipe blooms (`--glow-band-color`), or `null` for the
 * uncomputed/unknown band (no glow on a neutral grey dot). The halo itself is
 * gated by the `--dot-glow` / `--glow-amount` tokens defined in index.css:
 * APP chrome carries a real blur, but the report island (`[data-theme-scope]`)
 * zeroes both, so a dot rendered inside a report yields NO glow. We still scope
 * the glow to the decision bands (go / conditional / conditional-high / no-go)
 * so a not-computed placeholder never blooms.
 */
export function bandGlowColorVar(
  color: DerivedSiteVerdict['bandColor'] | null | undefined,
): string | null {
  if (color === 'green') return 'var(--primary)';
  if (color === 'amber') return 'var(--warning)';
  if (color === 'orange') return 'var(--conditional-high)';
  if (color === 'red') return 'var(--danger)';
  return null;
}

export function bandToneClass(
  color: DerivedSiteVerdict['bandColor'] | null | undefined,
): string {
  if (color === 'green') return 'text-primary';
  if (color === 'amber') return 'text-warning';
  // Aurora: single theme-aware --conditional-high token replaces the
  // light/dark orange pair; no need for a dark: variant since the token
  // value already swaps per theme.
  if (color === 'orange') return 'text-conditional-high';
  if (color === 'red') return 'text-danger';
  return 'text-text-muted';
}

/**
 * Map a derived-verdict band colour to a StatusPill variant so the hero
 * pill cannot drift from the band/dot. Critically, a CONDITIONAL band
 * (orange) maps to `warn`, NOT `success` — the prior hand-rolled ternary in
 * OverviewTab could show green for a conditional band. (U5a)
 */
export function bandStatusPillVariant(
  color: DerivedSiteVerdict['bandColor'] | null | undefined,
): StatusPillVariant {
  if (color === 'green') return 'success';
  if (color === 'amber') return 'warn';
  // Aurora: the CONDITIONAL-HIGH band gets its own pill variant (still NOT
  // 'success' — see the U5a note above; a conditional band must never read as
  // a clean go) so the hero pill tone matches the band/dot colour exactly.
  if (color === 'orange') return 'conditional-high';
  if (color === 'red') return 'danger';
  return 'neutral';
}

export function tierColorClass(tier: VerdictTier | null | undefined): string {
  if (tier === 'green') return 'bg-primary';
  if (tier === 'amber') return 'bg-warning';
  if (tier === 'red') return 'bg-danger';
  return 'bg-text-muted/30';
}

export function tierToneClass(tier: VerdictTier | null | undefined): string {
  if (tier === 'green') return 'text-primary';
  if (tier === 'amber') return 'text-warning';
  if (tier === 'red') return 'text-danger';
  return 'text-text-muted';
}
